import { parseClassFile } from "./classfile";
import type { ClassInfo, RecoveredExpression } from "./types";

const MAX_CLASS_REWRITE_BYTES = 32 * 1024 * 1024;

interface ConstantPoolLayout {
  count: number;
  end: number;
  utf8: Map<number, string>;
}

interface MethodLayout {
  index: number;
  name: string;
  descriptor: string;
  codeAttribute:
    | {
        start: number;
        end: number;
        nameIndex: number;
        infoStart: number;
        codeStart: number;
        codeLength: number;
      }
    | null;
}

export interface NeutralizedMethod {
  bytes: Uint8Array;
  method: string;
  descriptor: string;
  originalCodeSize: number;
  replacementCodeSize: number;
}

export interface InlinedRecoveredStrings {
  bytes: Uint8Array;
  attempted: number;
  eligible: number;
  applied: number;
  subsumed: number;
  skipped: number;
}

export interface RemovedClassFields {
  bytes: Uint8Array;
  removed: number;
  names: string[];
}

function assertAvailable(bytes: Uint8Array, offset: number, length: number) {
  if (offset < 0 || length < 0 || offset + length > bytes.length) {
    throw new Error("The class file is truncated.");
  }
}

function u2(bytes: Uint8Array, offset: number) {
  assertAvailable(bytes, offset, 2);
  return (bytes[offset] << 8) | bytes[offset + 1];
}

function u4(bytes: Uint8Array, offset: number) {
  assertAvailable(bytes, offset, 4);
  return (
    bytes[offset] * 0x1000000 +
    (bytes[offset + 1] << 16) +
    (bytes[offset + 2] << 8) +
    bytes[offset + 3]
  );
}

function encodeU2(value: number) {
  return Uint8Array.from([(value >>> 8) & 0xff, value & 0xff]);
}

function encodeU4(value: number) {
  return Uint8Array.from([
    (value >>> 24) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 8) & 0xff,
    value & 0xff,
  ]);
}

function join(chunks: Uint8Array[]) {
  const length = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }
  return output;
}

function owned(bytes: Uint8Array) {
  const output = new Uint8Array(bytes.length);
  output.set(bytes);
  return output;
}

function decodeModifiedUtf8(bytes: Uint8Array) {
  let value = "";
  for (let index = 0; index < bytes.length; ) {
    const first = bytes[index++];
    if ((first & 0x80) === 0) {
      value += String.fromCharCode(first);
      continue;
    }
    if ((first & 0xe0) === 0xc0 && index < bytes.length) {
      const second = bytes[index++];
      value +=
        (second & 0xc0) === 0x80
          ? String.fromCharCode(((first & 0x1f) << 6) | (second & 0x3f))
          : "\ufffd";
      continue;
    }
    if ((first & 0xf0) === 0xe0 && index + 1 < bytes.length) {
      const second = bytes[index++];
      const third = bytes[index++];
      value +=
        (second & 0xc0) === 0x80 && (third & 0xc0) === 0x80
          ? String.fromCharCode(
              ((first & 0x0f) << 12) |
                ((second & 0x3f) << 6) |
                (third & 0x3f),
            )
          : "\ufffd";
      continue;
    }
    value += "\ufffd";
  }
  return value;
}

function encodeModifiedUtf8(value: string) {
  const output: number[] = [];
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code === 0) {
      output.push(0xc0, 0x80);
    } else if (code <= 0x7f) {
      output.push(code);
    } else if (code <= 0x7ff) {
      output.push(0xc0 | (code >>> 6), 0x80 | (code & 0x3f));
    } else {
      output.push(
        0xe0 | (code >>> 12),
        0x80 | ((code >>> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return Uint8Array.from(output);
}

function scanConstantPool(bytes: Uint8Array): ConstantPoolLayout {
  if (bytes.length < 10 || u4(bytes, 0) !== 0xcafebabe) {
    throw new Error("Invalid JVM class-file magic.");
  }
  if (bytes.length > MAX_CLASS_REWRITE_BYTES) {
    throw new Error("The class exceeds the 32 MB rewrite limit.");
  }

  const count = u2(bytes, 8);
  const utf8 = new Map<number, string>();
  let offset = 10;
  for (let index = 1; index < count; index += 1) {
    assertAvailable(bytes, offset, 1);
    const tag = bytes[offset++];
    if (tag === 1) {
      const length = u2(bytes, offset);
      offset += 2;
      assertAvailable(bytes, offset, length);
      utf8.set(index, decodeModifiedUtf8(bytes.subarray(offset, offset + length)));
      offset += length;
    } else if (tag === 3 || tag === 4) {
      offset += 4;
    } else if (tag === 5 || tag === 6) {
      offset += 8;
      index += 1;
    } else if ([7, 8, 16, 19, 20].includes(tag)) {
      offset += 2;
    } else if ([9, 10, 11, 12, 17, 18].includes(tag)) {
      offset += 4;
    } else if (tag === 15) {
      offset += 3;
    } else {
      throw new Error(`Unsupported constant-pool tag ${tag}.`);
    }
    assertAvailable(bytes, offset, 0);
  }
  return { count, end: offset, utf8 };
}

function skipAttributes(bytes: Uint8Array, offset: number, count: number) {
  let cursor = offset;
  for (let index = 0; index < count; index += 1) {
    assertAvailable(bytes, cursor, 6);
    const length = u4(bytes, cursor + 2);
    cursor += 6 + length;
    assertAvailable(bytes, cursor, 0);
  }
  return cursor;
}

function scanMethods(bytes: Uint8Array, pool: ConstantPoolLayout) {
  let offset = pool.end;
  assertAvailable(bytes, offset, 8);
  offset += 6;
  const interfaceCount = u2(bytes, offset);
  offset += 2 + interfaceCount * 2;
  assertAvailable(bytes, offset, 2);

  const fieldCount = u2(bytes, offset);
  offset += 2;
  for (let index = 0; index < fieldCount; index += 1) {
    assertAvailable(bytes, offset, 8);
    const attributeCount = u2(bytes, offset + 6);
    offset = skipAttributes(bytes, offset + 8, attributeCount);
  }

  assertAvailable(bytes, offset, 2);
  const methodCount = u2(bytes, offset);
  offset += 2;
  const methods: MethodLayout[] = [];
  for (let index = 0; index < methodCount; index += 1) {
    assertAvailable(bytes, offset, 8);
    const name = pool.utf8.get(u2(bytes, offset + 2)) ?? "";
    const descriptor = pool.utf8.get(u2(bytes, offset + 4)) ?? "";
    const attributeCount = u2(bytes, offset + 6);
    let attributeOffset = offset + 8;
    let codeAttribute: MethodLayout["codeAttribute"] = null;
    for (let attribute = 0; attribute < attributeCount; attribute += 1) {
      assertAvailable(bytes, attributeOffset, 6);
      const nameIndex = u2(bytes, attributeOffset);
      const length = u4(bytes, attributeOffset + 2);
      const end = attributeOffset + 6 + length;
      assertAvailable(bytes, end, 0);
      if (pool.utf8.get(nameIndex) === "Code") {
        const infoStart = attributeOffset + 6;
        assertAvailable(bytes, infoStart, 8);
        const codeLength = u4(bytes, infoStart + 4);
        const codeStart = infoStart + 8;
        assertAvailable(bytes, codeStart, codeLength);
        codeAttribute = {
          start: attributeOffset,
          end,
          nameIndex,
          infoStart,
          codeStart,
          codeLength,
        };
      }
      attributeOffset = end;
    }
    methods.push({ index, name, descriptor, codeAttribute });
    offset = attributeOffset;
  }
  return methods;
}

function returnBytecode(descriptor: string) {
  const marker = descriptor[descriptor.lastIndexOf(")") + 1];
  if (!marker || marker === "V") {
    return { maxStack: 0, code: Uint8Array.of(0xb1) };
  }
  if (marker === "J") {
    return { maxStack: 2, code: Uint8Array.of(0x09, 0xad) };
  }
  if (marker === "D") {
    return { maxStack: 2, code: Uint8Array.of(0x0e, 0xaf) };
  }
  if (marker === "F") {
    return { maxStack: 1, code: Uint8Array.of(0x0b, 0xae) };
  }
  if (marker === "L" || marker === "[") {
    return { maxStack: 1, code: Uint8Array.of(0x01, 0xb0) };
  }
  return { maxStack: 1, code: Uint8Array.of(0x03, 0xac) };
}

export function listClassMethods(bytes: Uint8Array) {
  const pool = scanConstantPool(bytes);
  return scanMethods(bytes, pool).map(({ index, name, descriptor, codeAttribute }) => ({
    index,
    name,
    descriptor,
    hasCode: Boolean(codeAttribute),
    protected: name === "<init>",
  }));
}

export function removeClassFields(
  bytes: Uint8Array,
  names: Iterable<string>,
): RemovedClassFields {
  const targets = new Set(names);
  if (!targets.size) {
    return { bytes: owned(bytes), removed: 0, names: [] };
  }
  const pool = scanConstantPool(bytes);
  let offset = pool.end;
  assertAvailable(bytes, offset, 8);
  offset += 6;
  const interfaceCount = u2(bytes, offset);
  offset += 2 + interfaceCount * 2;
  assertAvailable(bytes, offset, 2);
  const fieldCountOffset = offset;
  const fieldCount = u2(bytes, offset);
  offset += 2;
  const kept: Uint8Array[] = [];
  const removedNames: string[] = [];
  for (let index = 0; index < fieldCount; index += 1) {
    const start = offset;
    assertAvailable(bytes, start, 8);
    const name = pool.utf8.get(u2(bytes, start + 2)) ?? "";
    const attributeCount = u2(bytes, start + 6);
    offset = skipAttributes(bytes, start + 8, attributeCount);
    if (targets.has(name)) {
      removedNames.push(name);
    } else {
      kept.push(bytes.subarray(start, offset));
    }
  }
  if (!removedNames.length) {
    return { bytes: owned(bytes), removed: 0, names: [] };
  }
  const output = join([
    bytes.subarray(0, fieldCountOffset),
    encodeU2(kept.length),
    ...kept,
    bytes.subarray(offset),
  ]);
  parseClassFile(output, "fields-pruned.class");
  return {
    bytes: output,
    removed: removedNames.length,
    names: removedNames,
  };
}

export function neutralizeClassMethod(
  bytes: Uint8Array,
  methodIndex: number,
): NeutralizedMethod {
  const pool = scanConstantPool(bytes);
  const target = scanMethods(bytes, pool).find(
    (method) => method.index === methodIndex,
  );
  if (!target) throw new Error("The selected method no longer exists.");
  if (target.name === "<init>") {
    throw new Error(
      "Constructors are protected because replacing them without a verified super call creates invalid bytecode.",
    );
  }
  if (!target.codeAttribute) {
    throw new Error("Abstract and native methods do not contain a body to remove.");
  }

  const { codeAttribute } = target;
  assertAvailable(bytes, codeAttribute.infoStart, 8);
  const maxLocals = u2(bytes, codeAttribute.infoStart + 2);
  const originalCodeSize = u4(bytes, codeAttribute.infoStart + 4);
  const replacement = returnBytecode(target.descriptor);
  const info = join([
    encodeU2(replacement.maxStack),
    encodeU2(maxLocals),
    encodeU4(replacement.code.length),
    replacement.code,
    encodeU2(0),
    encodeU2(0),
  ]);
  const attribute = join([
    encodeU2(codeAttribute.nameIndex),
    encodeU4(info.length),
    info,
  ]);
  const output = join([
    bytes.subarray(0, codeAttribute.start),
    attribute,
    bytes.subarray(codeAttribute.end),
  ]);

  parseClassFile(output, "neutralized.class");
  return {
    bytes: output,
    method: target.name,
    descriptor: target.descriptor,
    originalCodeSize,
    replacementCodeSize: replacement.code.length,
  };
}

export function rewriteClassSymbols(
  bytes: Uint8Array,
  replacer: (value: string) => string,
) {
  const pool = scanConstantPool(bytes);
  const count = u2(bytes, 8);
  const chunks: Uint8Array[] = [bytes.subarray(0, 10)];
  let offset = 10;
  let changed = 0;

  for (let index = 1; index < count; index += 1) {
    const start = offset;
    const tag = bytes[offset++];
    if (tag === 1) {
      const length = u2(bytes, offset);
      offset += 2;
      const original = decodeModifiedUtf8(bytes.subarray(offset, offset + length));
      offset += length;
      const replaced = replacer(original);
      if (replaced !== original) {
        const encoded = encodeModifiedUtf8(replaced);
        if (encoded.length > 0xffff) {
          throw new Error("A rewritten constant-pool string exceeds 65,535 bytes.");
        }
        chunks.push(Uint8Array.of(1), encodeU2(encoded.length), encoded);
        changed += 1;
      } else {
        chunks.push(bytes.subarray(start, offset));
      }
    } else {
      const size =
        tag === 3 || tag === 4
          ? 4
          : tag === 5 || tag === 6
            ? 8
            : [7, 8, 16, 19, 20].includes(tag)
              ? 2
              : [9, 10, 11, 12, 17, 18].includes(tag)
                ? 4
                : tag === 15
                  ? 3
                  : -1;
      if (size < 0) throw new Error(`Unsupported constant-pool tag ${tag}.`);
      offset += size;
      assertAvailable(bytes, offset, 0);
      chunks.push(bytes.subarray(start, offset));
      if (tag === 5 || tag === 6) index += 1;
    }
  }
  if (!changed) return { bytes: new Uint8Array(bytes), changedConstants: 0 };
  chunks.push(bytes.subarray(pool.end));
  const output = join(chunks);
  parseClassFile(output, "rewritten.class");
  return { bytes: output, changedConstants: changed };
}

interface StringInlineCandidate {
  methodIndex: number;
  startOffset: number;
  endOffset: number;
  value: string;
}

function branchTargets(method: ClassInfo["methods"][number]) {
  const targets = new Set<number>();
  for (const instruction of method.instructions) {
    if (
      !/^(?:if|goto|jsr|tableswitch|lookupswitch)/.test(
        instruction.mnemonic,
      )
    ) {
      continue;
    }
    for (const match of instruction.operands.matchAll(/-?\d+/g)) {
      const value = Number(match[0]);
      if (Number.isInteger(value)) targets.add(value);
    }
  }
  return targets;
}

function protectedCodeOffsets(
  bytes: Uint8Array,
  attribute: NonNullable<MethodLayout["codeAttribute"]>,
) {
  const protectedOffsets = new Set<number>();
  const exceptionTable = attribute.codeStart + attribute.codeLength;
  assertAvailable(bytes, exceptionTable, 2);
  const count = u2(bytes, exceptionTable);
  let cursor = exceptionTable + 2;
  for (let index = 0; index < count; index += 1) {
    assertAvailable(bytes, cursor, 8);
    protectedOffsets.add(u2(bytes, cursor));
    protectedOffsets.add(u2(bytes, cursor + 2));
    protectedOffsets.add(u2(bytes, cursor + 4));
    cursor += 8;
  }
  return protectedOffsets;
}

function collectInlineCandidates(
  classInfo: ClassInfo,
  recovered: RecoveredExpression[],
) {
  const proposed: StringInlineCandidate[] = [];
  let rejected = 0;
  for (const item of recovered) {
    if (
      !item.patchable ||
      item.confidence !== "high" ||
      item.className !== classInfo.name ||
      item.sequenceStartOffset === undefined ||
      item.value.length === 0 ||
      item.value.length > 16_384
    ) {
      continue;
    }
    const sequenceStartOffset = item.sequenceStartOffset;
    for (let methodIndex = 0; methodIndex < classInfo.methods.length; methodIndex += 1) {
      const method = classInfo.methods[methodIndex];
      if (
        method.name !== item.method ||
        (item.methodDescriptor && method.descriptor !== item.methodDescriptor)
      ) {
        continue;
      }
      const callIndex = method.instructions.findIndex(
        (instruction) => instruction.offset === item.offset,
      );
      const startIndex = method.instructions.findIndex(
        (instruction) => instruction.offset === sequenceStartOffset,
      );
      if (callIndex < 0 || startIndex < 0 || startIndex > callIndex) {
        continue;
      }
      const call = method.instructions[callIndex];
      if (
        !/^(?:invokevirtual|invokespecial|invokestatic|invokeinterface|invokedynamic)$/.test(
          call.mnemonic,
        )
      ) {
        continue;
      }
      const endOffset =
        method.instructions[callIndex + 1]?.offset ?? method.codeSize;
      if (endOffset - sequenceStartOffset < 3) continue;
      const guarded = new Set([
        ...branchTargets(method),
      ]);
      if (
        [...guarded].some(
          (offset) =>
            offset > sequenceStartOffset && offset < endOffset,
        )
      ) {
        continue;
      }
      proposed.push({
        methodIndex,
        startOffset: sequenceStartOffset,
        endOffset,
        value: item.value,
      });
      break;
    }
  }
  proposed.sort(
    (left, right) =>
      right.endOffset -
        right.startOffset -
        (left.endOffset - left.startOffset) ||
      left.methodIndex - right.methodIndex ||
      left.startOffset - right.startOffset,
  );
  const candidates: StringInlineCandidate[] = [];
  for (const candidate of proposed) {
    const overlaps = candidates.some(
      (accepted) =>
        accepted.methodIndex === candidate.methodIndex &&
        candidate.startOffset < accepted.endOffset &&
        candidate.endOffset > accepted.startOffset,
    );
    if (overlaps) {
      rejected += 1;
      continue;
    }
    candidates.push(candidate);
  }
  return { candidates, rejected };
}

function appendStringConstants(bytes: Uint8Array, values: string[]) {
  const pool = scanConstantPool(bytes);
  const indices = new Map<string, number>();
  const entries: Uint8Array[] = [];
  let count = pool.count;
  for (const value of [...new Set(values)]) {
    const encoded = encodeModifiedUtf8(value);
    if (!encoded.length || encoded.length > 0xffff || count + 2 > 0xffff) {
      continue;
    }
    const utf8Index = count;
    const stringIndex = count + 1;
    entries.push(
      join([Uint8Array.of(1), encodeU2(encoded.length), encoded]),
      join([Uint8Array.of(8), encodeU2(utf8Index)]),
    );
    indices.set(value, stringIndex);
    count += 2;
  }
  if (!entries.length) return { bytes: owned(bytes), indices };
  return {
    bytes: join([
      bytes.subarray(0, 8),
      encodeU2(count),
      bytes.subarray(10, pool.end),
      ...entries,
      bytes.subarray(pool.end),
    ]),
    indices,
  };
}

/**
 * Replaces a proven constant decrypt-call sequence with one LDC_W instruction
 * and NOP padding. Code length and every later bytecode offset remain stable,
 * so branch tables, exception tables and StackMapTable offsets do not need to
 * be rewritten. Candidates with an interior branch or exception boundary are
 * deliberately skipped.
 */
export function inlineRecoveredStrings(
  bytes: Uint8Array,
  classInfo: ClassInfo,
  recovered: RecoveredExpression[],
): InlinedRecoveredStrings {
  const attempted = recovered.filter(
    (item) =>
      item.patchable &&
      item.confidence === "high" &&
      item.className === classInfo.name,
  ).length;
  const collected = collectInlineCandidates(classInfo, recovered);
  const candidates = collected.candidates;
  if (!candidates.length) {
    return {
      bytes: owned(bytes),
      attempted,
      eligible: 0,
      applied: 0,
      subsumed: 0,
      skipped: attempted,
    };
  }
  const augmented = appendStringConstants(
    bytes,
    candidates.map((candidate) => candidate.value),
  );
  const pool = scanConstantPool(augmented.bytes);
  const methods = scanMethods(augmented.bytes, pool);
  const output = owned(augmented.bytes);
  let applied = 0;
  const subsumed = collected.rejected;
  let skipped = Math.max(0, attempted - candidates.length - subsumed);

  for (const candidate of candidates) {
    const method = methods[candidate.methodIndex];
    const constantIndex = augmented.indices.get(candidate.value);
    const attribute = method?.codeAttribute;
    if (!attribute || !constantIndex) {
      skipped += 1;
      continue;
    }
    const protectedOffsets = protectedCodeOffsets(output, attribute);
    if (
      [...protectedOffsets].some(
        (offset) =>
          offset > candidate.startOffset && offset < candidate.endOffset,
      )
    ) {
      skipped += 1;
      continue;
    }
    const start = attribute.codeStart + candidate.startOffset;
    const end = attribute.codeStart + candidate.endOffset;
    if (
      candidate.startOffset < 0 ||
      candidate.endOffset > attribute.codeLength ||
      end - start < 3
    ) {
      skipped += 1;
      continue;
    }
    output.fill(0x00, start, end);
    output[start] = 0x13;
    output[start + 1] = (constantIndex >>> 8) & 0xff;
    output[start + 2] = constantIndex & 0xff;
    applied += 1;
  }

  parseClassFile(output, "string-inlined.class");
  return {
    bytes: output,
    attempted,
    eligible: candidates.length,
    applied,
    subsumed,
    skipped,
  };
}
